import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import authUtils from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Extract session_id from URL - handles various browser quirks
 * Safari/iOS may delay hash fragment availability or encode it differently
 */
function extractSessionId() {
  // Try multiple sources for the hash
  const hashSources = [
    window.location.hash,
    document.location.hash,
  ];
  
  for (const rawHash of hashSources) {
    if (!rawHash) continue;
    
    // Remove leading #
    const hash = rawHash.startsWith('#') ? rawHash.substring(1) : rawHash;
    if (!hash) continue;
    
    // Try parsing as URL params
    try {
      const params = new URLSearchParams(hash);
      const sessionId = params.get('session_id');
      if (sessionId) {
        console.log('[AuthCallback] Found session_id via URLSearchParams');
        return sessionId;
      }
    } catch (e) {
      console.log('[AuthCallback] URLSearchParams parse failed:', e);
    }
    
    // Manual extraction fallback - some browsers encode the = sign
    const match = hash.match(/session_id[=:]([^&]+)/);
    if (match) {
      console.log('[AuthCallback] Found session_id via regex');
      return decodeURIComponent(match[1]);
    }
  }
  
  // Also check the full URL in case hash wasn't properly separated
  const fullUrl = window.location.href;
  const hashIndex = fullUrl.indexOf('#');
  if (hashIndex !== -1) {
    const hashPart = fullUrl.substring(hashIndex + 1);
    const match = hashPart.match(/session_id[=:]([^&]+)/);
    if (match) {
      console.log('[AuthCallback] Found session_id from full URL');
      return decodeURIComponent(match[1]);
    }
  }
  
  return null;
}

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('Processing...');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (hasProcessed.current) return;

    const processAuth = async () => {
      let sessionId = null;
      
      try {
        // Debug logging
        const userAgent = navigator.userAgent;
        const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
        const isIOS = /iPad|iPhone|iPod/.test(userAgent);
        
        console.log('[AuthCallback] Full URL:', window.location.href);
        console.log('[AuthCallback] Hash from location:', location.hash);
        console.log('[AuthCallback] Hash from window:', window.location.hash);
        console.log('[AuthCallback] User Agent:', userAgent);
        console.log('[AuthCallback] Is Safari:', isSafari);
        console.log('[AuthCallback] Is iOS:', isIOS);
        console.log('[AuthCallback] Retry count:', retryCount);
        
        // Extract session ID using our robust function
        sessionId = extractSessionId();
        
        // Safari sometimes needs a moment for the hash to be available
        if (!sessionId && retryCount < 3) {
          console.log('[AuthCallback] No session ID yet, retrying in 500ms...');
          setStatus('Completing authentication...');
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            hasProcessed.current = false;
          }, 500);
          return;
        }

        if (!sessionId) {
          console.log('[AuthCallback] No session ID found after retries');
          toast.error('Authentication data not received. Please try again.');
          navigate('/login');
          return;
        }

        hasProcessed.current = true;
        setStatus('Verifying...');
        
        console.log('[AuthCallback] Session ID found, length:', sessionId.length);
        console.log('[AuthCallback] Session ID first 20 chars:', sessionId.substring(0, 20));
        console.log('[AuthCallback] Sending session exchange request...');
        
        // Log the exact request being sent for debugging
        const requestBody = { session_id: sessionId };
        console.log('[AuthCallback] Request body:', JSON.stringify(requestBody));
        
        const response = await axios.post(
          `${API}/auth/session`,
          requestBody,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        console.log('[AuthCallback] Session response:', {
          hasToken: !!response.data.session_token,
          requires2fa: response.data.requires_2fa,
          email: response.data.email
        });

        // Store session token in localStorage for Safari/iOS compatibility
        if (response.data.session_token) {
          authUtils.setToken(response.data.session_token);
          console.log('[AuthCallback] Token stored successfully');
        }

        // Check if 2FA is required
        if (response.data.requires_2fa) {
          toast.info(`Verification code sent to ${response.data.email}`);
          navigate('/verify-otp', {
            state: { 
              email: response.data.email,
              name: response.data.name
            },
            replace: true
          });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        console.error('[AuthCallback] Error:', error);
        console.error('[AuthCallback] Error details:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        });
        
        let message = 'Authentication failed';
        
        if (error.response?.data?.detail) {
          message = error.response.data.detail;
          
          // Provide more helpful messages for common errors
          if (message === 'Invalid session ID') {
            message = 'Session expired or invalid. Please try logging in again.';
            console.log('[AuthCallback] Session ID was:', sessionId?.substring(0, 20) + '...');
          }
        } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network')) {
          message = 'Network error. Please check your connection and try again.';
        }
        
        toast.error(message);
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate, location, retryCount]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700 dark:border-indigo-400 mx-auto"></div>
        <p className="mt-4 text-slate-600 dark:text-slate-300" data-testid="auth-callback-status">{status}</p>
        {retryCount > 0 && (
          <p className="mt-2 text-xs text-slate-400">Attempt {retryCount + 1} of 4</p>
        )}
      </div>
    </div>
  );
}

export default AuthCallback;